import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { LadderChip } from '../../SchedulerPage/components/LadderChip/LadderChip';
import type { useActivityFilters } from '../hooks/useActivityFilters';
import { hidesEveryRung, soleRung, toggleIssuesOnly, toggleSoleRung, type ISummary } from './workPlanModel';
import styles from './workPlanSummary.module.css';

export interface IWorkPlanSummaryProps {
  summary: ISummary;
  /** The page's filters — each count sets them, as the filter panel does. */
  f: ReturnType<typeof useActivityFilters>;
}

/** The rungs that need a look, in the ladder's order. */
const LOOK_RUNGS = ['inProgress', 'delayed', 'overdue'] as const;

function Fact({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" className={styles.fact} aria-pressed={pressed} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * The window in one line: the rungs that need a look, last week's PPC and the
 * issues. Each count is a filter over the Status checkboxes — the page's own,
 * so it shows in the panel (and its badge) and holds across the views: a rung
 * shows that rung alone and no issue, the issues uncheck every rung (the lane
 * alone). Pressed again, it puts the boxes back to their default.
 */
export function WorkPlanSummary({ summary, f }: IWorkPlanSummaryProps) {
  const { t } = useTranslation();
  const hidden = f.filters.hiddenStatuses;
  const sole = soleRung(hidden);
  return (
    <div role="group" aria-label={t('activities.workPlan.summary.label')} className={styles.summary}>
      {LOOK_RUNGS.map((rung) => (
        <Fact key={rung} pressed={sole === rung} onClick={() => f.setHiddenStatuses(toggleSoleRung(hidden, rung))}>
          <LadderChip rung={rung} asSwatch />
          {t(`activities.workPlan.summary.${rung}`, { count: summary[rung] })}
        </Fact>
      ))}
      {/* PPC is honest only if done is marked — the hint says what it counts. */}
      <Tooltip content={t('activities.workPlan.summary.ppcHint')}>
        <span className={styles.fact}>
          {t('activities.workPlan.summary.ppc', { value: summary.ppc ? `${summary.ppc.percent}%` : '—' })}
        </span>
      </Tooltip>
      <Fact pressed={hidesEveryRung(hidden)} onClick={() => f.setHiddenStatuses(toggleIssuesOnly(hidden))}>
        {t('activities.workPlan.summary.issues', { count: summary.issues })}
      </Fact>
    </div>
  );
}
